import dayjs from 'dayjs';

import type { Checklist, ChecklistStatus } from '@cognite/apm-client';
import type { Discipline } from '@cognite/apm-config';

import type {
  GridFilterItem,
  GridFilterOptionsType,
} from '@cognite/cogs-lab-v11';
import type { UserProfileItem } from '@cognite/sdk';
import type { SolutionTag } from '@cognite/solution-tags';
import type { TFunction } from 'i18next';

import { truncateText } from '../../../../utils/truncateText';
import { isServicePrincipal } from '../../../user/utils/user-profile-utils';
import { MissingChecklistsState, type SchedulerChecklist } from '../types';
import { DAY_GROUP_ROW_ID_PREFIX } from './format-data-grid-data-utils';
import type { DateRangeType } from '../../../ui/date-range-picker/types';

export const isDateRangeFilterForScheduledChecklistsValid = (
  dateRange: DateRangeType
) => {
  const [startDate, endDate] = dateRange;

  if (!startDate || !endDate) return false;

  const today = dayjs().endOf('day');
  const maxDateRange = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

  const isStartDateValid = startDate.isBefore(today) || startDate.isSame(today);
  const isDateRangeValid = endDate.diff(startDate) <= maxDateRange;

  return isStartDateValid && isDateRangeValid;
};

// If the end date is in the future, update it to today
export const getDateRangeFilterWithoutFutureEndDate = (
  dateRange: DateRangeType
): DateRangeType => {
  const [startDate, endDate] = dateRange;
  if (!startDate || !endDate) return [null, null];

  const today = dayjs().endOf('day');

  if (endDate.isAfter(today)) {
    return [startDate, dayjs(today)];
  }

  return dateRange;
};

export const getUserIdentifiers = (
  checklists: SchedulerChecklist[]
): {
  createdByIds: string[];
  assigneeIds: string[];
  disciplineIds: string[];
} => {
  const assigneeSet = new Set<string>();
  const createdBySet = new Set<string>();
  const disciplineIdsSet = new Set<string>();

  for (const checklist of checklists) {
    // Add createdBy user identifier
    if (checklist.createdBy?.externalId) {
      createdBySet.add(checklist.createdBy.externalId);
    }

    // Add assignedTo user identifiers (excluding disciplines)
    if (checklist.assignedTo) {
      for (const id of checklist.assignedTo) {
        if (id && !id.startsWith('discipline')) {
          assigneeSet.add(id);
        }
      }
    }

    if (checklist.assignedTo) {
      for (const id of checklist.assignedTo) {
        if (id?.startsWith('discipline:')) {
          disciplineIdsSet.add(id);
        }
      }
    }
  }

  return {
    createdByIds: Array.from(createdBySet),
    assigneeIds: Array.from(assigneeSet),
    disciplineIds: Array.from(disciplineIdsSet),
  };
};

/**
 * Filters a user profiles map to include only profiles whose user identifiers match the provided IDs
 * @param userProfilesMap - Record mapping keys to UserProfileItem objects
 * @param ids - Array of user identifiers to filter by
 * @returns Filtered record containing only user profiles that match the IDs
 */
export const filterUserProfilesMap = (
  userProfilesMap: Record<string, UserProfileItem>,
  ids: string[]
): Record<string, UserProfileItem> => {
  const filteredMap: Record<string, UserProfileItem> = {};

  Object.entries(userProfilesMap).forEach(([key, profile]) => {
    if (ids.includes(profile.userIdentifier)) {
      filteredMap[key] = profile;
    }
  });

  return filteredMap;
};

/**
 * Extracts discipline IDs from an array of identifiers
 * @param ids - Array of identifiers that may contain discipline IDs
 * @returns Array of discipline IDs with 'discipline:' prefix removed
 */
export const extractDisciplineIds = (ids: string[]): string[] => {
  return ids.map((id) =>
    id?.startsWith('discipline:') ? id.replace('discipline:', '') : id
  );
};

export const getDisciplinesUserProfiles = (
  disciplineIds: string[],
  configuredDisciplines?: Discipline[]
): UserProfileItem[] => {
  // Convert discipline IDs to UserProfileItem objects
  const disciplines: UserProfileItem[] = [];
  for (const id of disciplineIds) {
    const configuredDiscipline = configuredDisciplines?.find(
      (discipline) => discipline.externalId === id
    );
    disciplines.push({
      displayName: configuredDiscipline?.name || `discipline:${id}`,
      userIdentifier: `discipline:${id}`,
      lastUpdatedTime: 0,
    });
  }

  return disciplines;
};

/**
 * Creates status filter options with dynamic counts based on checklist data
 * @param checklists - Array of checklists to calculate counts from
 * @returns Filter options with calculated counts
 */
export const getSchedulesStatusOptionsWithCounts = (
  checklists: SchedulerChecklist[],
  statusOptions: { label: string; value: ChecklistStatus }[]
): { label: string; value: ChecklistStatus; count: number }[] => {
  const statusCounts = checklists.reduce(
    (counts, { status }) =>
      status ? { ...counts, [status]: (counts[status] || 0) + 1 } : counts,
    Object.fromEntries(statusOptions.map(({ value }) => [value, 0]))
  );

  return statusOptions.map((option) => ({
    ...option,
    count: statusCounts[option.value],
  }));
};

/**
 * Type for extracting field values from checklist items
 */
export type FieldExtractor<T> = (item: T) => string[];

/**
 * Calculates counts for field values across checklist items
 * @param items - Array of items to count from
 * @param fieldExtractor - Function to extract field values from each item
 * @returns Record mapping field values to their counts
 */
const calculateFieldCounts = <T>(
  items: T[],
  fieldExtractor: FieldExtractor<T>
): Record<string, number> => {
  return items.reduce<Record<string, number>>((counts, item) => {
    const fieldValues = fieldExtractor(item);
    fieldValues.forEach((value) => {
      if (value) {
        counts[value] = (counts[value] || 0) + 1;
      }
    });
    return counts;
  }, {});
};

/**
 * Creates a single filter option from user profile and count
 * @param profile - User profile to create option from
 * @param count - Count value for this option
 * @param defaultDisplayLabel - Label when displayName is absent, and for service principals
 * @param maxLabelLength - Maximum length for label truncation
 * @returns Filter option with label, value, and count
 */
const createFilterOption = (
  profile: UserProfileItem,
  count: number,
  defaultDisplayLabel: string,
  maxLabelLength: number
): GridFilterOptionsType => {
  const isServicePrincipalProfile = isServicePrincipal(profile);

  const fullLabel = isServicePrincipalProfile
    ? defaultDisplayLabel
    : (profile.displayName ?? defaultDisplayLabel);

  const label =
    fullLabel.length > maxLabelLength
      ? truncateText(fullLabel, maxLabelLength)
      : fullLabel;

  return {
    label,
    value: profile.userIdentifier,
    count,
  };
};

/**
 * Sorts filter options alphabetically by label
 * @param options - Array of filter options to sort
 * @returns Sorted array of filter options
 */
const sortFilterOptions = (
  options: GridFilterOptionsType[]
): GridFilterOptionsType[] => {
  return options.sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Merges current user profiles map with selected filters map
 * @param currentMap - Current user profiles from data
 * @param selectedFiltersMap - Map of selected filter user profiles to preserve
 * @returns Merged user profiles map without duplicates
 */
export const mergeUserProfilesWithSelectedFilters = (
  currentMap: Record<string, UserProfileItem>,
  selectedFiltersMap: Record<string, UserProfileItem>
): Record<string, UserProfileItem> => {
  const allUserIds = new Set([
    ...Object.keys(selectedFiltersMap),
    ...Object.keys(currentMap),
  ]);

  const mergedMap: Record<string, UserProfileItem> = {};

  allUserIds.forEach((userId) => {
    // Prioritize current map over selected filters map
    mergedMap[userId] = currentMap[userId] || selectedFiltersMap[userId];
  });

  return mergedMap;
};

/**
 * Creates filter options from user profiles map with dynamic field counts
 * @param userProfilesMap - Record mapping user identifiers to UserProfileItem objects
 * @param checklists - Array of checklists to calculate counts from
 * @param defaultDisplayLabel - Label when displayName is absent, and for service principals
 * @param fieldExtractor - Function to extract the field values to count (e.g., assignedTo, createdBy)
 * @param selectedFiltersMap - Map of selected filter user profiles to preserve
 * @returns Array of filter options with label, value, and count for each user
 */
export const getSchedulesFilterOptionsWithCounts = (
  userProfilesMap: Record<string, UserProfileItem>,
  checklists: SchedulerChecklist[],
  defaultDisplayLabel: string,
  fieldExtractor: FieldExtractor<SchedulerChecklist>,
  selectedFiltersMap: Record<string, UserProfileItem> = {}
): GridFilterOptionsType[] => {
  const MAX_LABEL_LENGTH = 25;

  // Merge current profiles with selected filters to ensure selected options remain available
  const mergedProfilesMap = mergeUserProfilesWithSelectedFilters(
    userProfilesMap,
    selectedFiltersMap
  );

  const fieldCounts = calculateFieldCounts(checklists, fieldExtractor);

  const options = Object.values(mergedProfilesMap).map((profile) =>
    createFilterOption(
      profile,
      fieldCounts[profile.userIdentifier] || 0,
      defaultDisplayLabel,
      MAX_LABEL_LENGTH
    )
  );

  return sortFilterOptions(options);
};

/**
 * Field extractor for assignedTo field
 * @param checklist - Checklist to extract assignedTo values from
 * @returns Array of assignee IDs
 */
export const extractAssignedToField = (
  checklist: SchedulerChecklist
): string[] => {
  return checklist.assignedTo || [];
};

/**
 * Field extractor for createdBy field
 * @param checklist - Checklist to extract createdBy value from
 * @returns Array containing createdBy ID (or empty array if not present)
 */
export const extractCreatedByField = (
  checklist: SchedulerChecklist
): string[] => {
  return checklist.createdBy?.externalId
    ? [checklist.createdBy.externalId]
    : [];
};

/**
 * Apply filter model to data array
 * @param data - Array of data objects to filter
 * @param filterModel - Filter model with items to apply
 * @returns Filtered data array
 */
export const applyDataGridFilters = <T extends Record<string, any>>(
  data: T[],
  filterModel: GridFilterItem[]
): T[] => {
  // Return all data if no filters applied
  if (!data || !filterModel || filterModel.length === 0) {
    return data;
  }

  return data.filter((row) => {
    return filterModel.every((filterItem: GridFilterItem) => {
      const { field, value } = filterItem;

      // Skip filter if no value specified
      if (!value || value.length === 0) {
        return true;
      }

      const rowValue = row[field];

      if (!rowValue) return false;

      return Array.isArray(rowValue)
        ? value.some((val: string) => rowValue.includes(val))
        : value.includes(rowValue);
    });
  });
};

const statusTranslationKeys = {
  Ready: 'CHECKLIST_STATUS_READY',
  'In progress': 'CHECKLIST_STATUS_IN_PROGRESS',
  Done: 'CHECKLIST_STATUS_DONE',
  Planned: 'CHECKLIST_STATUS_PLANNED',
} as const satisfies Record<ChecklistStatus, string>;

/**
 * Get translated status label for a checklist status
 * @param status - The checklist status to translate
 * @param t - Translation function
 * @returns Translated status label
 */
export const getStatusChipLabel = (
  status: ChecklistStatus,
  t: TFunction<'checklist'>
): string => t(statusTranslationKeys[status], status);

/**
 * Type guard to check if the checklist creation state is currently creating checklists
 * @param creationState - The current missing checklists state to check
 * @returns True if the state is CREATING_CHECKLISTS, false otherwise
 */
export const isChecklistCreatingState = (
  creationState?: MissingChecklistsState
): creationState is MissingChecklistsState.CREATING_CHECKLISTS => {
  return creationState === MissingChecklistsState.CREATING_CHECKLISTS;
};

/**
 * Converts a date range to ISO strings
 * @param selectedDateFilter - The date range to convert, contains start and end dates as Dayjs objects
 * @returns An object containing the from and until ISO strings
 */
export const getDateFilters = (
  selectedDateFilter: DateRangeType
): { fromISO: string; untilISO: string } => {
  let fromISO: string;
  let untilISO: string;
  try {
    const [start, end] = selectedDateFilter;
    if (!start || !end) {
      throw new Error('Invalid date filter');
    }
    fromISO = start.utc().toISOString();
    untilISO = end.utc().toISOString();
  } catch (_error) {
    throw new Error('Invalid date filter');
  }
  return { fromISO, untilISO };
};

/**
 * Handles row click by calling the provided callback with the row ID converted to a string
 * Only calls the callback if both the callback and ID are defined
 * @param id - The row ID
 * @param onRowClick - Optional callback to invoke with the string ID
 */
export const handleRowClickSafely = (
  id: string | number | null | undefined,
  onRowClick?: (id: string) => void
): void => {
  if (
    onRowClick &&
    id != null &&
    !String(id)?.startsWith(DAY_GROUP_ROW_ID_PREFIX)
  ) {
    onRowClick(String(id));
  }
};

/**
 * Finds a checklist from an array by matching externalId
 * @param checklists - Array of scheduler checklists to search
 * @param externalId - The external ID to match
 * @returns The matching checklist as Checklist type, or undefined if not found
 */
export const findChecklistByExternalId = (
  checklists: SchedulerChecklist[],
  externalId: string
): Checklist | undefined => {
  const searchedChecklist = checklists.find(
    (checklist) => checklist.externalId === externalId
  );

  if (!searchedChecklist) return undefined;

  // Transform SchedulerChecklist to Checklist by filtering out solution tags without names
  const validSolutionTags = searchedChecklist.solutionTags?.filter(
    (tag): tag is SolutionTag => tag.name !== undefined
  );

  return {
    ...searchedChecklist,
    solutionTags: validSolutionTags,
  };
};

/**
 * Type guard to check if a checklist ID is present (not null or undefined)
 * @param checklistId - The checklist ID to check
 * @returns True if the checklist ID is a non-null string
 */
export const isChecklistIdPresent = (
  checklistId: string | null | undefined
): checklistId is NonNullable<typeof checklistId> => {
  return Boolean(checklistId);
};
